import React, { useMemo, useCallback, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import PageLayout from "../../../_layout/PageLayout";
import { EditOrganizationInfo } from "../../../../services/authorization/Permissions";
import { SettingsMenuItemsPages, SettingsMenuItemsModel } from "../../SettingsMenuItems";
import { CurrentOrganizationStorageModel } from "../../../../services/organization/CurrentOrganizationStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import OrganizationInput from "../../../../services/organization/OrganizationInput";
import { FormikValues, FormikHelpers } from "formik";
import {
  SubSection,
  DataSection,
  EditDescriptionSection,
} from "../../../../components/data-components/sections/DataSections";
import Form from "../../../../components/form/Form";
import {
  EditOrganizationName,
  EditOrganizationContactDetails,
  DisplayOrganizationCountry,
} from "../components/DataComponents";
import "./EditOrganization.scss";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import { currentUserService } from "../../../../services/user/CurrentUserStorage";
import { api } from "../../../../services/api/ApiService";
import {
  EditDropdownField,
  EditTextField,
  EditDropdownSuggestionField,
} from "../../../my-account/request-organization/DataComponents";
import { AvailableCountriesStorageModel } from "../../../../services/organization/AvailableCountriesStorage";
import { translationService } from "../../../../services/translation/TranslationService";
import { warningLeavePageService } from "../../../../services/page/WarningLeavePageService";
import {
  AddressCleansingServiceModel,
  AddressEntity,
} from "../../../../services/address/AddressCleansingService";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [currentOrganizationStorage] = useState(() =>
    CurrentOrganizationStorageModel.create({}, { api })
  );

  const [availableCountriesStorage] = useState(() =>
    AvailableCountriesStorageModel.create({}, { api })
  );

  const [addressCleansingService] = useState(() =>
    AddressCleansingServiceModel.create({}, { api })
  );

  const menuItems = useMemo(() => {
    const settingsMenuItems = SettingsMenuItemsModel.create({
      menuItemPage: SettingsMenuItemsPages.EditOrganization,
    });

    return {
      staticItems: settingsMenuItems.getStaticItems(),
    };
  }, []);

  const initialValues = useMemo(() => {
    const organization = currentOrganizationStorage.organization;

    const organizationContactDetails = organization.contact ? organization.contact : null;
    const organizationContactDetailsUserName = {
      id: organizationContactDetails?.userId,
      name: organizationContactDetails?.name,
    };
    const organizationContactDetailsUserEmail = {
      id: organizationContactDetails?.userId,
      name: organizationContactDetails?.email,
    };

    return {
      id: organization.id,
      name: organization.name,
      description: organization.description,
      contactUserId: organizationContactDetails?.userId,
      contactUserName: organizationContactDetailsUserName,
      contactUserEmail: organizationContactDetailsUserEmail,
      phone: organization.phone || "",
      vat: organization.vat || "",
      country: organization.countryCode,
      address: organization.address,
      addressNumber: organization.addressNumber,
      zip: organization.zip,
      city: organization.city,
      state: organization.state,
      generalManagerFirstName: organization.generalManagerFirstName,
      generalManagerLastName: organization.generalManagerLastName,
      websiteURL: organization.websiteURL,
    };
  }, [currentOrganizationStorage.organization]);

  const availableCountries = useMemo(() => {
    return availableCountriesStorage.getAvailableCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCountriesStorage.isLoading, translationService.language]);

  const goToOrganizationPage = useCallback(
    () => history.push(`/settings/organization-details`),
    [history]
  );

  const handleSubmit = async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
    const organizationToEdit: OrganizationInput = {
      id: currentOrganizationStorage.organization.id,
      name: values.name,
      description: values.description,
      contactUserId: values.contactUserId,
      phone: values.phone,
      country: values.country,
      address: values.address,
      addressNumber: values.addressNumber,
      zip: values.zip,
      city: values.city,
      state: values.state,
      vat: values.vat,
      generalManagerFirstName: values.generalManagerFirstName,
      generalManagerLastName: values.generalManagerLastName,
      websiteURL: values.websiteURL,
    };
    try {
      await currentOrganizationStorage.editOrganization(organizationToEdit);
      warningLeavePageService.setIsDirty(false);
      await currentUserService.load();
      return goToOrganizationPage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  return (
    <div className="edit-organization">
      <PageLayout
        id="view-organization-details"
        accessWithOneOfThePermissions={[EditOrganizationInfo]}
        titleKey="ViewOrganization.title"
        menuItems={menuItems}
        isLoading={currentOrganizationStorage.isLoading || availableCountriesStorage.isLoading}
      >
        <Form
          formikOptions={{
            initialValues: initialValues,
            onSubmit: handleSubmit,
            validateOnBlur: false,
            validateOnChange: false,
            enableReinitialize: true,
          }}
          onCancel={goToOrganizationPage}
        >
          {({ values, setFieldValue }) => {
            return (
              <>
                <DataSection labelKey="general.organization.details">
                  <SubSection hideRightBorder>
                    <EditOrganizationName name={values.name} />
                    {!currentOrganizationStorage.organization.hasPaidSubscription && (
                      <EditDropdownField
                        id="organization-country"
                        labelKey="organization.DisplayOrganizationCountry"
                        required
                        fieldName="country"
                        name={values.country}
                        options={availableCountries}
                        setSelectedItem={(item) => {
                          setFieldValue("country", item ?? "");
                        }}
                      />
                    )}
                    {currentOrganizationStorage.organization.hasPaidSubscription && (
                      <DisplayOrganizationCountry
                        code={currentOrganizationStorage.organization.countryCode}
                      />
                    )}
                    <EditDropdownSuggestionField
                      id="organization-address"
                      labelKey="organization.DisplayOrganizationAddress"
                      placeholderKey="organization.EditOrganizationAddress.placeholder"
                      required
                      fieldName="address"
                      fieldValue={values.address}
                      performSearch={(query) =>
                        addressCleansingService.search(values.country, query)
                      }
                      searchTextChanged={(query) => (values.address = query)}
                      selectedItemChanged={(item: AddressEntity) => {
                        setFieldValue("address", item.street);
                        setFieldValue("zip", item.zip);
                        setFieldValue("city", item.city);
                        setFieldValue("state", item.state);
                      }}
                      clearCurrentItem={() => setFieldValue("address", "")}
                    />
                    <EditTextField
                      id="organization-address-number"
                      labelKey="organization.DisplayOrganizationAddressNumber"
                      name={values.addressNumber}
                      fieldName="addressNumber"
                      required
                    />
                    <EditDropdownSuggestionField
                      id="organization-zip"
                      labelKey="organization.DisplayOrganizationZip"
                      placeholderKey="organization.EditOrganizationZip.placeholder"
                      required
                      fieldName="zip"
                      fieldValue={values.zip}
                      performSearch={(query) =>
                        addressCleansingService.search(values.country, query)
                      }
                      searchTextChanged={(query) => (values.zip = query)}
                      selectedItemChanged={(item: AddressEntity) => {
                        setFieldValue("address", item.street);
                        setFieldValue("zip", item.zip);
                        setFieldValue("city", item.city);
                        setFieldValue("state", item.state);
                      }}
                      clearCurrentItem={() => setFieldValue("zip", "")}
                    />
                    <EditDropdownSuggestionField
                      id="organization-city"
                      labelKey="organization.DisplayOrganizationCity"
                      placeholderKey="organization.EditOrganizationCity.placeholder"
                      required
                      fieldName="city"
                      fieldValue={values.city}
                      performSearch={(query) =>
                        addressCleansingService.search(values.country, query)
                      }
                      searchTextChanged={(query) => (values.city = query)}
                      selectedItemChanged={(item: AddressEntity) => {
                        setFieldValue("address", item.street);
                        setFieldValue("zip", item.zip);
                        setFieldValue("city", item.city);
                        setFieldValue("state", item.state);
                      }}
                      clearCurrentItem={() => setFieldValue("city", "")}
                    />
                    <EditTextField
                      id="organization-state"
                      labelKey="organization.DisplayOrganizationState"
                      name={values.state}
                      placeholderKey="organization.EditOrganizationState.placeholder"
                      fieldName="state"
                    />
                    <EditTextField
                      id="organization-vatNumber"
                      labelKey="organization.DisplayOrganizationVat"
                      name={values.vat}
                      placeholderKey="organization.EditOrganizationVAT.placeholder"
                      fieldName="vat"
                      required={
                        currentOrganizationStorage.organization.hasPaidSubscription &&
                        currentOrganizationStorage.organizationIsNonGerman
                      }
                    />
                  </SubSection>
                  <SubSection>
                    <EditTextField
                      id="general-manager-first-name"
                      labelKey="general.general-manager-first-name"
                      name={values.generalManagerFirstName}
                      placeholderKey="general.general-manager-first-name-placeholder"
                      required
                      fieldName="generalManagerFirstName"
                    />
                    <EditTextField
                      id="general-manager-last-name"
                      labelKey="general.general-manager-last-name"
                      name={values.generalManagerLastName}
                      placeholderKey="general.general-manager-last-name-placeholder"
                      required
                      fieldName="generalManagerLastName"
                    />
                    <EditTextField
                      id="phone"
                      labelKey="organization.organization-phone-number"
                      placeholderKey="organization.organization-phone-number-placeholder"
                      name={values.phone}
                      required
                      fieldName="phone"
                    />
                    <EditTextField
                      id="website-url"
                      labelKey="general.website-url"
                      placeholderKey="organization.EditOrganizationWebsiteURL.placeholder"
                      name={values.websiteURL}
                      fieldName="websiteURL"
                    />
                  </SubSection>
                  <SubSection>
                    <EditOrganizationContactDetails
                      contactUserName={values.contactUserName}
                      contactUserEmail={values.contactUserEmail}
                      setFieldValue={setFieldValue}
                    />
                    <DisplayDateCreated date={currentOrganizationStorage.organization.created} />
                    <DisplayCreatedBy
                      createdBy={currentOrganizationStorage.organization.createdBy.name}
                    />
                  </SubSection>
                </DataSection>
                <EditDescriptionSection description={values.description} />
              </>
            );
          }}
        </Form>
      </PageLayout>
    </div>
  );
});
